import { addDays, type IsoDate } from './dates';
import { findCookable } from './cook-search';
import type { Pantry } from './pantry';
import type { Plan, PlannedDay } from './plan';
import { replaceDinner } from './planner/edit';
import { recipeOf, shoppingContext, type PlanRequest } from './planner/request';
import { productOf } from './price-book';
import type { Recipe } from './recipe';
import { tripDates } from './shopping';

// DE: Ab dieser Haltbarkeit darf ein Abendessen ohne Verlust verschoben werden.
// EN: From this shelf life on, a dinner can be postponed without waste.
const LONG_LASTING_DAYS = 90;

export interface SwapResult {
  plan: Plan;
  movedTo: IsoDate | null;
  useSoon: string[];
}

/**
 * DE: Schnelle erlaubte Ersatzgerichte: zuerst der Notfallvorrat, dann nach dem, was zu Hause ist.
 * EN: Quick allowed replacement dinners: the backup kit first, then by what is at home.
 */
export function quickAlternatives(
  plan: Plan,
  date: IsoDate,
  request: PlanRequest,
  pantry: Pantry,
): Recipe[] {
  const current = plan.days.find((day) => day.date === date)?.dinner.recipeId;
  const { household, book, recipes, staples } = request;
  const dinners = recipes.filter((recipe) => recipe.meal === 'dinner' && recipe.id !== current);
  const context = {
    recipes: dinners,
    members: household.members,
    book,
    date,
    storeIds: household.storeIds,
    quick: household.quick,
    staples,
  };
  const ranked = findCookable(pantry, context, { quickOnly: true }).map((match) =>
    recipeOf(request, match.recipeId),
  );
  return [
    ...ranked.filter((recipe) => recipe.backupKit),
    ...ranked.filter((recipe) => !recipe.backupKit),
  ];
}

function perishables(recipe: Recipe, request: PlanRequest): string[] {
  return recipe.ingredients
    .map((ingredient) => ingredient.productId)
    .filter((id) => productOf(request.book.catalog, id).shelfDays < LONG_LASTING_DAYS);
}

function postponableDay(plan: Plan, date: IsoDate, request: PlanRequest): PlannedDay | undefined {
  const { period } = request;
  const nextTrip =
    tripDates(shoppingContext(request)).find((trip) => trip > date) ??
    addDays(period.start, period.days);
  return plan.days.find(
    (day) =>
      day.date > date &&
      day.date < nextTrip &&
      !day.dinner.locked &&
      perishables(recipeOf(request, day.dinner.recipeId), request).length === 0,
  );
}

/**
 * DE: „Heute keine Kraft“: das Abendessen ersetzen und das ursprüngliche Gericht auf den nächsten
 * Abend vor dem Einkauf verschieben, dessen Gericht nur lange haltbare Zutaten hat.
 * EN: "No energy today": replace the dinner and move the original one to the next evening before
 * the shopping trip whose dinner uses only long-lasting ingredients.
 */
export function applyNoEnergySwap(
  plan: Plan,
  date: IsoDate,
  replacementId: string,
  request: PlanRequest,
): SwapResult {
  const today = plan.days.find((day) => day.date === date);
  if (!today) throw new Error(`No planned day on ${date}`);
  const original = recipeOf(request, today.dinner.recipeId);
  const swapped = replaceDinner(plan, today, recipeOf(request, replacementId), request);
  const target = postponableDay(swapped, date, request);
  if (!target) return { plan: swapped, movedTo: null, useSoon: perishables(original, request) };
  return {
    plan: replaceDinner(swapped, target, original, request),
    movedTo: target.date,
    useSoon: [],
  };
}
